import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { assertNonEmptyString, assertOneOf } from "../../internal/contracts";
import { useThemeA } from "../../internal/ThemeAContext";
import {
  BUTTON_ICON_CLASS,
  BUTTON_SPINNER_CLASS,
  iconActionClassName,
} from "./buttonStyles";

const INTENTS = ["primary", "secondary", "quiet", "destructive"];
const TOOLTIP_DELAY_MS = 600;
const TOOLTIP_GAP = 8;

const NAMED_ICONS = Object.freeze({
  activity: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  analyze: <><path d="M4 19V9" /><path d="M10 19V5" /><path d="M16 19v-7" /><path d="M22 19H2" /></>,
  archive: <><path d="M3 6h18" /><path d="M5 6v14h14V6" /><path d="M9 10h6" /><path d="M4 3h16v3H4z" /></>,
  delete: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 14H6L5 6" /><path d="M10 11v5M14 11v5" /></>,
  dismiss: <><path d="m6 6 12 12" /><path d="M18 6 6 18" /></>,
  download: <><path d="M12 4v12" /><path d="m7 11 5 5 5-5" /><path d="M5 20h14" /></>,
  duplicate: <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" /></>,
  // Aide contextuelle (« i » cerclé) : ouvre une explication de l'écran.
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></>,
  environment: <><path d="M4 21v-7" /><path d="M4 10V3" /><path d="M12 21v-9" /><path d="M12 8V3" /><path d="M20 21v-5" /><path d="M20 12V3" /><path d="M1 14h6" /><path d="M9 8h6" /><path d="M17 16h6" /></>,
  manage: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></>,
  mobile: <><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></>,
  monitor: <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" /><circle cx="12" cy="12" r="2.5" /></>,
  open: <><path d="m9 18 6-6-6-6" /></>,
  pause: <><path d="M9 5v14" /><path d="M15 5v14" /></>,
  profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  qa: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" /></>,
  resume: <><path d="m8 5 11 7-11 7Z" /></>,
  restore: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></>,
  results: <><path d="M4 19h16" /><path d="M6 16v-5" /><path d="M12 16V6" /><path d="M18 16V9" /></>,
  share: <><circle cx="18" cy="5" r="2" /><circle cx="6" cy="12" r="2" /><circle cx="18" cy="19" r="2" /><path d="m8 11 8-5" /><path d="m8 13 8 5" /></>,
  treatment: <><path d="M9 3v5a3 3 0 0 0 6 0V3" /><path d="M7 3h4" /><path d="M13 3h4" /><path d="M12 11v3a5 5 0 0 0 10 0v-1" /><circle cx="22" cy="11" r="1" /></>,
  upload: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M5 20h14" /></>,
});

const renderIcon = (icon) => Object.prototype.hasOwnProperty.call(NAMED_ICONS, icon) ? (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="akv:h-[1.125rem] akv:w-[1.125rem]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {NAMED_ICONS[icon]}
  </svg>
) : icon;

function useDelayedTooltip(actionRef) {
  const tooltipId = useId();
  const timerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const cancelPending = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const close = () => {
    cancelPending();
    setTooltip(null);
  };

  const openAfterDelay = () => {
    cancelPending();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      const rect = actionRef.current?.getBoundingClientRect();
      if (!rect) return;
      setTooltip({
        left: rect.left + (rect.width / 2),
        top: rect.top - TOOLTIP_GAP,
      });
    }, TOOLTIP_DELAY_MS);
  };

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);
  useEffect(() => {
    if (!tooltip) return undefined;
    const closeVisibleTooltip = () => setTooltip(null);
    window.addEventListener("resize", closeVisibleTooltip);
    window.addEventListener("scroll", closeVisibleTooltip, true);
    return () => {
      window.removeEventListener("resize", closeVisibleTooltip);
      window.removeEventListener("scroll", closeVisibleTooltip, true);
    };
  }, [tooltip]);

  return { close, openAfterDelay, tooltip, tooltipId };
}

/**
 * Compact row action with one invariant interaction model: icon at rest,
 * accessible name immediately, and a delayed visible label on hover/focus.
 * `href` keeps native link behaviour; without it the action is a button.
 */
export function IconAction({
  disabled = false,
  href,
  icon,
  intent = "quiet",
  label,
  loading = false,
  loadingLabel,
  onClick,
  rel,
  target,
  type = "button",
}) {
  useThemeA("IconAction");
  assertOneOf("IconAction", "intent", intent, INTENTS);
  assertNonEmptyString("IconAction", "label", label);
  if (loading) assertNonEmptyString("IconAction", "loadingLabel", loadingLabel);

  const actionRef = useRef(null);
  const { close, openAfterDelay, tooltip, tooltipId } = useDelayedTooltip(actionRef);
  const tooltipRoot = typeof document !== "undefined"
    ? actionRef.current?.closest('[data-akongo-ui="v2"]') || document.body
    : null;
  const unavailable = disabled || loading;
  const accessibleLabel = loading ? loadingLabel : label;
  const commonProps = {
    "aria-busy": loading || undefined,
    "aria-describedby": tooltip ? tooltipId : undefined,
    "aria-label": accessibleLabel,
    className: iconActionClassName(intent),
    "data-ak-v2-component": "icon-action",
    onBlur: close,
    onFocus: openAfterDelay,
    onMouseEnter: openAfterDelay,
    onMouseLeave: close,
    ref: actionRef,
  };
  const content = loading
    ? <span className={BUTTON_SPINNER_CLASS} aria-hidden="true" />
    : <span className={BUTTON_ICON_CLASS} aria-hidden="true">{renderIcon(icon)}</span>;

  const action = href ? (
    <a
      {...commonProps}
      aria-disabled={unavailable || undefined}
      href={href}
      onClick={(event) => {
        if (unavailable) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      rel={rel}
      tabIndex={unavailable ? -1 : undefined}
      target={target}
    >
      {content}
    </a>
  ) : (
    <button {...commonProps} disabled={unavailable} onClick={onClick} type={type}>
      {content}
    </button>
  );

  return (
    <span className="akv:inline-flex akv:flex-none akv:flex-nowrap">
      {action}
      {tooltip && tooltipRoot ? createPortal(
        <span
          id={tooltipId}
          role="tooltip"
          data-ak-v2-component="action-tooltip"
          className="akv:pointer-events-none akv:fixed akv:z-[10000] akv:max-w-64 akv:-translate-x-1/2 akv:-translate-y-full akv:rounded-ak-control akv:bg-ak-text akv:px-2.5 akv:py-1.5 akv:font-ak-body akv:text-xs akv:font-semibold akv:leading-4 akv:text-ak-surface akv:shadow-ak-overlay"
          style={{ left: tooltip.left, top: tooltip.top }}
        >
          {accessibleLabel}
        </span>,
        tooltipRoot
      ) : null}
    </span>
  );
}

IconAction.propTypes = {
  disabled: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node.isRequired,
  intent: PropTypes.oneOf(INTENTS),
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  target: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
};
